import { DateTime } from 'luxon'
import safe from 'safe-await'
import { handleEmptyDate } from '@/helpers/utils'

const endpoint = (append = '') => `${process.env.VUE_APP_PROVIDER}${append}`

export const getProvisionOptionsByCarData = async (car) => {
  // Keys to exclude (avoids request bloat)
  const exclude = ['carmodel', 'item_addons', 'item_files', 'item_mobilede_data', 'provisions', 'website_url']
  const carData = {}

  Object.keys(car).forEach((key) => {
    // Skip excluded keys
    if (exclude.includes(key)) return

    // Massage specific values for the backend, so that filtering is handled correctly
    switch (key) {
      // Values that are empty, but should be "0" instead for the backend
      case 'item_stocknumber':
        carData[key] = car[key] || '0'
        break

      // Dates, which should be in the format dd.MM.yyyy
      case 'item_initialregistration':
      case 'item_initialregistration_on_customer':
      case 'item_deregistration':
      case 'item_orderdate':
      case 'item_expected_dispatch':
      case 'item_invoicedate_purchased':
      case 'item_invoicedealine_purchased':
      case 'item_invoicedate_sold':
        if (handleEmptyDate(car[key]) === '') {
          carData[key] = ''
        } else {
          const date = DateTime.fromSQL(car[key])

          carData[key] = date.isValid ? date.toFormat('dd.MM.yyyy') : ''
        }

        break

      // The dispatch date time is handled in two separate fields
      case 'item_dispatch':
        if (handleEmptyDate(car[key]) === '') {
          carData[key] = ''
        } else {
          const date = DateTime.fromSQL(car[key])

          if (date.isValid) {
            carData.item_dispatch_date = date.toFormat('dd.MM.yyyy')
            carData.item_dispatch_time = date.toFormat('HH:mm')
          } else {
            carData.item_dispatch_date = ''
            carData.item_dispatch_time = ''
          }
        }

        break

      default:
        carData[key] = car[key]
    }
  })

  // If we have an existing car, make sure to include item_car
  if (car.id) {
    carData.item_car = car.id
  }

  if (car.item_mobilede_data) {
    carData.item_mobilede_data_trimline = car.item_mobilede_data.trimline
    carData.item_mobilede_data_fuel = car.item_mobilede_data.fuel
  }

  const { data } = await axios.post(endpoint('/provisions/provisionoptionsbycardata'), carData)

  return data
}

export const getUsedProvisionOptions = async (carId) => {
  const { data } = await window.axios.get(endpoint(`/provisions/getusedprovisionoptions/${carId}`))

  return data
}

export const updateProvisionItems = async (carId, provisions) => {
  const { data } = await window.axios.post(endpoint(`/provisions/updateprovisionitems/${carId}`), {
    carId,
    provisions
  })

  return data
}

export const addProvisionItems = async (carId, provisions) => {
  const { data } = await window.axios.post(endpoint('/provisions/addprovisionitems'), {
    carId,
    provisions
  })

  return data
}

export const getProvisionAccountings = async (carId) => {
  const { data } = await window.axios.get(endpoint(`/provisions/getprovisionaccountings/${carId}`))

  return data
}

export const getPoolPayouts = async (carId) => {
  const { data } = await window.axios.get(endpoint(`/provisions/getpoolpayouts/${carId}`))

  return data
}

export const getPaidImportResults = async () => {
  const { data } = await window.axios.get(endpoint('/provisions/getpaidimportresults'))

  return data
}

export const getProvisionFilters = async (provisionId) => {
  const { data } = await window.axios.get(endpoint(`/provisions/getprovisionfilters/${provisionId}`))

  return data
}

export const getProvisionFilter = async (filterId) => {
  const { data } = await window.axios.get(endpoint(`/provisions/getprovisionfilter/${filterId}`))

  return data
}

export const addProvisionFilter = async (provisionId, payload) => {
  const { data } = await window.axios.post(endpoint(`/provisions/addprovisionfilter/${provisionId}`), payload)

  return data
}

export const updateProvisionFilter = async (provisionId, filterId, payload) => {
  const { data } = await window.axios.post(endpoint(`/provisions/updateprovisionfilter/${provisionId}/${filterId}`), payload)

  return data
}

export const deleteProvisionFilter = async (filterId) => {
  const { data } = await window.axios.post(endpoint(`/provisions/deleteprovisionfilter/${filterId}`))

  return data
}

export const addProvision = async (payload) => {
  const { data } = await window.axios.post(endpoint('/provisions/addprovision'), payload)

  return data
}

export const updateProvision = async (provisionId, payload) => {
  const { data } = await window.axios.post(endpoint(`/provisions/updateprovision/${provisionId}`), payload, {
    headers: {
      'content-type': 'multipart/form-data'
    }
  })

  return data
}

export const getProvision = async (provisionId) => {
  const { data } = await window.axios.get(endpoint(`/provisions/getprovision/${provisionId}`))

  return data
}

export const getProvisionItems = async (provisionId) => {
  const { data } = await window.axios.get(endpoint(`/provisions/getprovisionitems/${provisionId}`))

  return data
}

export const getUsedProvisions = async (carId) => {
  const { data } = await window.axios.get(endpoint(`/ajax/json_provision_used/${carId}`))

  return data
}

export const getActiveProvisionOptions = async () => {
  const [errors, response] = await safe(window.axios.get(endpoint('/provisions/getactiveprovisionoptions')))

  return errors ? [errors, undefined] : [undefined, response.data]
}

export const getProvisionOptionGroupsByCarData = async (car) => {
  // Keys to exclude (avoids request bloat)
  const exclude = ['carmodel', 'item_addons', 'item_files', 'item_mobilede_data', 'provisions', 'website_url', 'item_configuration']
  const carData = {}

  Object.keys(car).forEach((key) => {
    // Skip excluded keys
    if (exclude.includes(key)) return

    // Massage specific values for the backend, so that filtering is handled correctly
    switch (key) {
      // Values that are empty, but should be "0" instead for the backend
      case 'item_stocknumber':
        carData[key] = car[key] || '0'
        break

      // Dates, which should be in the format dd.MM.yyyy
      case 'item_initialregistration':
      case 'item_initialregistration_on_customer':
      case 'item_deregistration':
      case 'item_orderdate':
      case 'item_expected_dispatch':
      case 'item_invoicedate_purchased':
      case 'item_invoicedealine_purchased':
      case 'item_invoicedate_sold':
        if (handleEmptyDate(car[key]) === '') {
          carData[key] = ''
        } else {
          const date = DateTime.fromSQL(car[key])

          carData[key] = date.isValid ? date.toFormat('dd.MM.yyyy') : ''
        }

        break

      // The dispatch date time is handled in two separate fields
      case 'item_dispatch':
        if (handleEmptyDate(car[key]) === '') {
          carData[key] = ''
        } else {
          const date = DateTime.fromSQL(car[key])

          if (date.isValid) {
            carData.item_dispatch_date = date.toFormat('dd.MM.yyyy')
            carData.item_dispatch_time = date.toFormat('HH:mm')
          } else {
            carData.item_dispatch_date = ''
            carData.item_dispatch_time = ''
          }
        }

        break

      default:
        carData[key] = car[key]
    }
  })

  // If we have an existing car, make sure to include item_car
  if (car.id) {
    carData.item_car = car.id
  }

  if (car.item_mobilede_data) {
    carData.item_mobilede_data_trimline = car.item_mobilede_data.trimline
    carData.item_mobilede_data_fuel = car.item_mobilede_data.fuel
  }

  const { data } = await axios.post(endpoint('/provisions/provisionoptiongroupsbycardata'), carData)

  return data
}

export function updateProvisionAutoBookingRules(provisionId, payload) {
  return safe(axios.post(endpoint(`/provisions/updateautobookingrules/${provisionId}`), payload))
}

export function getProvisionAutoBookingSettings(provisionId) {
  return safe(axios.get(endpoint(`/provisions/getautobookingsettings/${provisionId}`)))
}

export function getDocumentCategories() {
  return safe(axios.get(endpoint('/provisions/getdocumentcategories')))
}

export function updateDocumentCategories(provisionId, payload) {
  return safe(axios.post(endpoint(`/provisions/updatedocumentcategories/${provisionId}`), payload, {
    headers: {
      'content-type': 'multipart/form-data'
    }
  }))
}

export function getPaidArchive(params = {}, options = {}) {
  return safe(axios.get(endpoint('/provisions/getpaidarchive'), {
    ...options,
    params
  }))
}

export function exportPaidArchive(payload) {
  return safe(axios.post(endpoint('/provisions/exportpaidarchive'), payload, { responseType: 'arraybuffer' }))
}

export function getPaidDocuments(params = {}) {
  return safe(axios.get(endpoint('/provisions/getpaiddocuments'), { params }))
}

export const exportProvisionItems = async (provisionId, params = {}) => {
  const [error, response] = await safe(window.axios.get(`${process.env.VUE_APP_PROVIDER}/provisions/exportprovisionitems/${provisionId}`, { params, responseType: 'arraybuffer' }))

  return error ? [error] : [error, response]
}

export default {
  addProvision,
  addProvisionFilter,
  addProvisionItems,
  deleteProvisionFilter,
  exportPaidArchive,
  exportProvisionItems,
  getActiveProvisionOptions,
  getDocumentCategories,
  getPaidArchive,
  getPaidDocuments,
  getPaidImportResults,
  getPoolPayouts,
  getProvision,
  getProvisionAccountings,
  getProvisionAutoBookingSettings,
  getProvisionFilter,
  getProvisionFilters,
  getProvisionItems,
  getProvisionOptionGroupsByCarData,
  getProvisionOptionsByCarData,
  getUsedProvisionOptions,
  getUsedProvisions,
  updateDocumentCategories,
  updateProvision,
  updateProvisionAutoBookingRules,
  updateProvisionFilter,
  updateProvisionItems
}
